// Pure payload-building + validation for the ITAM-2583 consult order.
// Extracted from React so the submission/validation logic is unit-testable
// without rendering. Consumed by useConsultOrderLifecycle (submit) and by
// ConsultOrderFormModal (live submit-enable + field-linked errors).

import {
  ConsultCnCOrderRequest,
  CnCOrderDeliveryRequest,
  CnCOrderRequest,
  ConsultOrderItemRequest,
  ConsultOrderPrefillResponse,
  UserAddressRequestPayload,
} from "../types/consultOrder.types";
import { ConsultRfzoOrderRequest } from "../types/consultRfzo.types";
import { ConsultOrderDraft } from "../contexts/ConsultOrderContext";
import { MarketplaceResponse } from "../types/shared";

/**
 * Which submit the delivery draft is validated/built for:
 *  - "CNC": the ITAM-2583 consult order (items + customer identity live here).
 *  - "RFZO_ORDER": the ITAM-2614 RFZO order step. Items and customer identity
 *    are READ-ONLY snapshots of the created therapy (D11) and are skipped;
 *    the delivery/address/personal-id rules stay (they ARE live at step 2).
 */
export type ConsultOrderValidationMode = "CNC" | "RFZO_ORDER";

export interface ConsultOrderMarketplaceInfo {
  marketplaceId: number;
  branchMarketplaceId: number;
}

export interface ConsultOrderValidationErrors {
  items?: string;
  firstName?: string;
  phoneNumber?: string;
  address?: string;
  personalId?: string;
  deliveryTimeSlot?: string;
  delivery?: string;
}

export interface ConsultOrderValidationResult {
  valid: boolean;
  errors: ConsultOrderValidationErrors;
}

/**
 * Stable identity for a delivery zone selection: the delivery type plus the
 * resolved address coordinates. DeliverySection tags each pre-check result with
 * this key; validation recomputes it from the CURRENT draft so a result whose
 * key no longer matches (address or type changed) is treated as unverified.
 */
export const deliveryZoneKey = (
  fulfillment: ConsultOrderDraft["fulfillment"],
  lat: number,
  lng: number
): string => `${fulfillment}:${lat}:${lng}`;

/** Loose plausibility check for a Serbian-ish phone (no OTP / no verification claim). */
export const isPlausiblePhone = (raw: string): boolean => {
  const value = raw.trim();
  if (!/^[+]?[\d\s()/-]{6,20}$/.test(value)) {
    return false;
  }
  const digits = value.replace(/\D/g, "");
  return digits.length >= 6 && digits.length <= 15;
};

/**
 * Resolve the delivery address into a submittable payload, or null when the
 * current draft has no valid (non-zero lat/lng) address for its mode.
 */
export const resolveAddressPayload = (
  draft: ConsultOrderDraft,
  prefill: ConsultOrderPrefillResponse | null
): UserAddressRequestPayload | null => {
  if (draft.addressMode === "NEW") {
    const a = draft.newAddress;
    if (!a || !a.name.trim() || !a.latitude || !a.longitude) {
      return null;
    }
    return a;
  }
  // SAVED
  if (draft.selectedAddressId == null || !prefill) {
    return null;
  }
  const found = prefill.addresses.find((x) => x.id === draft.selectedAddressId);
  if (!found || !found.latitude || !found.longitude) {
    return null;
  }
  return {
    id: found.id,
    name: found.name,
    number: found.number,
    apartment: found.apartment,
    floor: found.floor,
    region: found.region,
    city: found.city,
    postalCode: found.postalCode,
    latitude: found.latitude,
    longitude: found.longitude,
    otherInformation: found.otherInformation,
  };
};

/**
 * Validate the draft for submit-time gating.
 *
 * `detailed` is OPTIONAL and acts as the RUNTIME-ENFORCEMENT flag (not a fee
 * source): when omitted (pure-logic callers) the zone + delivery-fee checks are
 * skipped; when supplied (the form + the lifecycle submit) they are enforced.
 *
 * Delivery fee: sourced from `draft.deliveryServiceFee` (resolved from the branch
 * SERVICE items by DeliverySection), NOT from the nullable
 * `detailed.deliveryPrice*`. Submission is blocked while the fee is still loading
 * or when it could not be resolved to a usable price (a genuine 0 is fine).
 *
 * Zone: a confirmed out-of-zone address always blocks; an unknown zone
 * (null, e.g. the expected VC-2 403) degrades to backend validation.
 */
export const validateConsultOrderDraft = (
  draft: ConsultOrderDraft,
  prefill: ConsultOrderPrefillResponse | null,
  detailed?: MarketplaceResponse | null,
  mode: ConsultOrderValidationMode = "CNC"
): ConsultOrderValidationResult => {
  const errors: ConsultOrderValidationErrors = {};

  if (mode === "CNC") {
    if (draft.items.length < 1) {
      errors.items = "Dodajte bar jedan artikal.";
    }
    if (!draft.firstName.trim()) {
      errors.firstName = "Ime je obavezno.";
    }
    const phone = draft.phoneNumber.trim();
    if (!phone) {
      errors.phoneNumber = "Broj telefona je obavezan.";
    } else if (!isPlausiblePhone(phone)) {
      errors.phoneNumber = "Neispravan broj telefona.";
    }
  }

  if (draft.fulfillment !== "PICKUP") {
    const addr = resolveAddressPayload(draft, prefill);
    if (!addr) {
      errors.address =
        "Izaberite ili unesite adresu za predaju sa koordinatama.";
    } else if (detailed !== undefined) {
      // Runtime callers (form + lifecycle) enforce the zone pre-check. Compare
      // the recorded result's key to THIS selection so a stale approval (a check
      // done for a different address/type) never passes, and a still-pending
      // check blocks. Only a fresh IN-ZONE result — or an UNAVAILABLE (null)
      // result, which degrades to backend validation — is allowed through.
      const key = deliveryZoneKey(
        draft.fulfillment,
        addr.latitude,
        addr.longitude
      );
      const check = draft.deliveryZoneCheck;
      if (!check || check.key !== key) {
        errors.address = "Sačekajte proveru zone pomoći pri preuzimanju za izabranu adresu.";
      } else if (check.inZone === false) {
        errors.address = "Adresa je van zone pomoći pri preuzimanju za izabrani tip.";
      }
    }
    if (detailed !== undefined) {
      // Fee comes from the resolved branch SERVICE item, keyed to the current
      // fulfillment. Block while loading or when the resolved fee is unusable;
      // a still-missing key means the resolution has not run/settled yet.
      const fee = draft.deliveryServiceFee;
      if (!fee || fee.key !== draft.fulfillment || fee.status === "loading") {
        errors.delivery = "Sačekajte učitavanje usluge rezervacije.";
      } else if (fee.status !== "ok" || typeof fee.price !== "number") {
        errors.delivery =
          "Usluga rezervacije za izabrani tip nije dostupna — nije moguće poslati.";
      }
    }
    if (!draft.personalId.trim()) {
      errors.personalId =
        "Lični identifikacioni broj je obavezan za pomoć pri preuzimanju.";
    }
    if (draft.fulfillment === "STANDARD_DELIVERY" && !draft.deliveryTimeSlot) {
      errors.deliveryTimeSlot = "Izaberite termin.";
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

/**
 * Effective delivery fee for the draft: the resolved branch SERVICE-item price,
 * counted only when it was resolved OK for the CURRENT fulfillment (the same
 * key/status gate as validation — a fee mirrored for a different delivery type
 * must not leak into the estimate while the re-resolution is in flight).
 * 0 for pickup / loading / stale / unresolved.
 */
export const draftDeliveryFee = (draft: ConsultOrderDraft): number => {
  if (draft.fulfillment === "PICKUP") {
    return 0;
  }
  const fee = draft.deliveryServiceFee;
  if (!fee || fee.key !== draft.fulfillment || fee.status !== "ok") {
    return 0;
  }
  return typeof fee.price === "number" ? fee.price : 0;
};

/**
 * Build the CnCOrderDeliveryRequest for the CURRENT (non-pickup) draft: the
 * RESOLVED SERVICE item id + price (from draft.deliveryServiceFee — NOT the
 * nullable detailed fee), an explicit boolean saveAddress, deliveryTimeSlot for
 * STANDARD only. Throws when the draft has no resolvable address (validation
 * runs first, so this is defense in depth). Shared by the CnC consult order and
 * the ITAM-2614 RFZO order step.
 */
export const buildDeliveryRequest = (
  draft: ConsultOrderDraft,
  prefill: ConsultOrderPrefillResponse
): CnCOrderDeliveryRequest => {
  if (draft.fulfillment === "PICKUP") {
    throw new Error("buildDeliveryRequest called for a pickup draft.");
  }
  const address = resolveAddressPayload(draft, prefill);
  if (!address) {
    throw new Error("Delivery selected without a resolvable address.");
  }
  const isNewAddress = address.id == null;
  const resolvedFee = draft.deliveryServiceFee;
  const delivery: CnCOrderDeliveryRequest = {
    deliveryType: draft.fulfillment,
    deliveryPaymentType: draft.paymentType,
    // Resolved SERVICE item id (deterministic backend resolution); null lets
    // the backend fall back to its own name/User-Agent heuristic — but ONLY on
    // the CnC endpoint. The RFZO order endpoint has NO fallback (D8), which is
    // why buildRfzoOrderPayload hard-rejects a null id for CASH.
    serviceItemId: resolvedFee?.serviceItemId ?? null,
    userAddressRequest: address,
    personalIdType: draft.personalIdType,
    personalId: draft.personalId.trim(),
    // Resolved SERVICE-item price (a genuine 0 is valid); validation guarantees
    // this is a usable number before submit is allowed.
    deliveryPrice: typeof resolvedFee?.price === "number" ? resolvedFee.price : 0,
    // Existing (id-bearing) address never re-saves; new address always carries
    // an explicit boolean (null would NPE the backend).
    saveAddress: isNewAddress ? draft.saveNewAddress : false,
  };
  const noteForDelivery = draft.noteForDelivery.trim();
  if (noteForDelivery) {
    delivery.noteForDelivery = noteForDelivery;
  }
  if (draft.fulfillment === "STANDARD_DELIVERY" && draft.deliveryTimeSlot) {
    delivery.deliveryTimeSlot = {
      start: draft.deliveryTimeSlot.start,
      end: draft.deliveryTimeSlot.end,
    };
  }
  return delivery;
};

/**
 * Build the exact ConsultCnCOrderRequest. Pickup omits cnCOrderDeliveryRequest
 * entirely; delivery includes the shared buildDeliveryRequest result, and every
 * item carries gift:false.
 */
export const buildConsultOrderPayload = (
  draft: ConsultOrderDraft,
  prefill: ConsultOrderPrefillResponse,
  marketplaceInfo: ConsultOrderMarketplaceInfo
): ConsultCnCOrderRequest => {
  const items: ConsultOrderItemRequest[] = draft.items.map((line) => ({
    itemId: line.item.id.itemId,
    quantity: line.quantity,
    gift: false as const,
  }));

  const cnCOrderRequest: CnCOrderRequest = {
    userId: prefill.user.id,
    marketplaceId: marketplaceInfo.marketplaceId,
    branchMarketplaceId: marketplaceInfo.branchMarketplaceId,
    userPhoneNumberForOrder: draft.phoneNumber.trim(),
  };

  const firstName = draft.firstName.trim();
  if (firstName) {
    cnCOrderRequest.firstName = firstName;
  }
  const secondName = draft.secondName.trim();
  if (secondName) {
    cnCOrderRequest.secondName = secondName;
  }
  const note = draft.note.trim();
  if (note) {
    cnCOrderRequest.note = note;
  }

  if (draft.fulfillment !== "PICKUP") {
    cnCOrderRequest.cnCOrderDeliveryRequest = buildDeliveryRequest(
      draft,
      prefill
    );
  }

  return {
    consultId: prefill.consultId,
    cnCOrderRequest,
    items,
  };
};

/**
 * ITAM-2614 step-2 body. D11: the backend reads ONLY cnCOrderDeliveryRequest —
 * pickup sends {} (never a delivery object without a full address, D8). CASH
 * delivery hard-requires a RESOLVED serviceItemId: this endpoint has no
 * name/User-Agent fallback (the "null lets the backend fall back" contract is
 * CnC-only), so a null id here would 500 the backend. Validation blocks that
 * upstream (fee status "ok" implies a non-null id); the throw is defense.
 */
export const buildRfzoOrderPayload = (
  draft: ConsultOrderDraft,
  prefill: ConsultOrderPrefillResponse
): ConsultRfzoOrderRequest => {
  if (draft.fulfillment === "PICKUP") {
    return {};
  }
  const delivery = buildDeliveryRequest(draft, prefill);
  if (delivery.deliveryPaymentType === "CASH" && delivery.serviceItemId == null) {
    throw new Error("RFZO order with CASH delivery requires a resolved serviceItemId.");
  }
  return { cnCOrderDeliveryRequest: delivery };
};
